// src/views/sops/SopReader.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import StatusChip from "../../components/StatusChip";
import { getProgressChipProps } from "../../status/StatusChipTones";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinearProgress from "@mui/material/LinearProgress";
import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton.jsx";
import { tokens } from "../../theme/tokens";
import { useTheme } from "@mui/material/styles";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import useGraphQLClient from "../../hooks/useGraphQLClient";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import TaskImageGalleryModal from "../../components/TaskImageGalleryModal";
import Markdown from "../../components/Markdown.jsx";
import { READ_PROJECT } from "../../graph/projects/queries";
import { READ_SOP_TASK_PROGRESS } from "../../graph/tasks/queries";
import {
  MARK_SOP_TASK_READ,
  MARK_SOP_TASK_UNDERSTOOD,
} from "../../graph/tasks/mutations";

import { UserRole } from "../../constants/userRoles.ts";
import { useResolvedTenantId } from "../../hooks/useResolvedTenantId";
import { useSOPApprovals } from "../../hooks/useSOPApprovals";
import SOPRejectDialog from "../../components/SOPRejectDialog";


const TASK_IMAGES_BASE = "/assets/images";

const normalizeImageUrl = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^(https?:)?\/\//i.test(s) || s.startsWith("data:")) return s;

  let clean = s.replace(/^\.?\/*public\//i, "");
  if (!clean.includes("/")) {
    clean = `${TASK_IMAGES_BASE.replace(/^\//, "")}/${clean}`;
  }
  if (!clean.startsWith("/")) clean = `/${clean}`;

  return clean;
};

const sortBySeq = (list = []) =>
  [...list].sort((a, b) => {
    if (a.sequence && b.sequence) return a.sequence.localeCompare(b.sequence);
    if (a.sequence) return -1;
    if (b.sequence) return 1;
    return String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
  });



export default function SopReader() {
  const client = useGraphQLClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const approvalReturnPath =
    location.state?.redirectPath || "/my-sop-approvals";
  const returnButtonLabel = approvalReturnPath.startsWith("/playbooks/read/")
    ? "Back to Playbook Flow"
    : approvalReturnPath === "/manageBusinessStructure"
      ? "Back to Business Structure"
      : approvalReturnPath === "/managePlaybooks"
        ? "Back to Playbooks"
        : approvalReturnPath === "/my-sops"
          ? "Back to My SOPs"
          : approvalReturnPath.includes("approval")
            ? "Back to Approvals"
            : "Back";
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { setSnackbar } = useSnackbar();
  
  const {
    user,
    tenantId,
    tenantName,
  } = useResolvedTenantId();

  const loggedInUserId = user?.id;

  const canEditSop =
    user?.role === UserRole.SYSTEM_ADMIN ||
    user?.role === UserRole.TENANT_ADMIN;

  const canApproveSop = user?.role === UserRole.TENANT_STAFF;

  const [project, setProject] = useState(null);
  const [taskProgress, setTaskProgress] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());

  const [imgOpen, setImgOpen] = useState(false);
  const [imgList, setImgList] = useState([]);
  const [imgStartIdx, setImgStartIdx] = useState(0);

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectSopId, setRejectSopId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const {
    myApprovalSOPs,
    approveSOP,
    rejectSOP,
  } = useSOPApprovals({
    tenantId,
    userId: loggedInUserId,
  });

  const isAssignedApprovalSop = myApprovalSOPs.some((sop) => sop.id === id);

  const tasks = useMemo(
    () =>
      sortBySeq(
        (project?.tasks || []).filter(
          (task) => task.isLatest !== false
        )
      ),
    [project]
  );

  const progressByTaskId = useMemo(() => {
    return taskProgress.reduce((acc, item) => {
      acc[item.taskId] = item;
      return acc;
    }, {});
  }, [taskProgress]);

  const understoodCount = tasks.filter(
    (task) => progressByTaskId[task.id]?.status === "UNDERSTOOD"
  ).length;

  const updatedCount = tasks.filter(
    (task) => progressByTaskId[task.id]?.status === "UPDATED"
  ).length;

  const total = tasks.length;
  const pct = total ? Math.round((understoodCount / total) * 100) : 0;
  const canSubmitApproval = total > 0 && understoodCount === total;

  const openImages = (images, start = 0) => {
    const normalized = (images || []).map(normalizeImageUrl);
    setImgList(normalized);
    setImgStartIdx(start);
    setImgOpen(true);
  };

  const loadProject = async () => {
    try {
      const { readSingleProjectById } = await client.request(READ_PROJECT, {
        id,
      });

      setProject(readSingleProjectById);
    } catch (e) {
      console.error("Failed to load SOP:", e);
    }
  };

  const loadTaskProgress = async () => {
    if (!tenantId || !id || !loggedInUserId) {
      setTaskProgress([]);
      return;
    }

    try {
      const { readSOPTaskProgress } = await client.request(
        READ_SOP_TASK_PROGRESS,
        {
          tenantId,
          sopId: id,
          userId: loggedInUserId,
        }
      );

      setTaskProgress(readSOPTaskProgress || []);
    } catch (e) {
      console.error("Failed to load SOP task progress:", e);
      setTaskProgress([]);
    }
  };

  useEffect(() => {
    loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadTaskProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id, loggedInUserId]);

  useEffect(() => {
    setExpandedIds((prev) => {
      const keep = new Set(tasks.map((t) => t.id));
      const next = new Set();

      prev.forEach((taskId) => {
        if (keep.has(taskId)) next.add(taskId);
      });

      return next;
    });
  }, [tasks]);

  const allExpanded = tasks.length > 0 && expandedIds.size === tasks.length;

  const handleMarkTaskRead = async (taskId) => {
    if (!tenantId || !project?.id || !loggedInUserId) return;

    try {
      await client.request(MARK_SOP_TASK_READ, {
        tenantId,
        sopId: project.id,
        taskId,
        userId: loggedInUserId,
      });

      await loadTaskProgress();
    } catch (e) {
      console.error("Failed to mark SOP task as read", e);
    }
  };

  const handleMarkUnderstood = async (taskId) => {
    if (!tenantId || !project?.id || !loggedInUserId) return;

    try {
      await client.request(MARK_SOP_TASK_UNDERSTOOD, {
        tenantId,
        sopId: project.id,
        taskId,
        userId: loggedInUserId,
      });

      await loadTaskProgress();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Step marked as understood",
      });
    } catch (e) {
      console.error("Failed to mark SOP task as understood", e);

      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to mark step as understood",
      });
    }
  };

  const handleToggleAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
      return;
    }

    const nextExpanded = new Set(tasks.map((task) => task.id));
    setExpandedIds(nextExpanded);

    tasks.forEach((task) => {
      handleMarkTaskRead(task.id);
    });
  };

 const handleApproveSop = async () => {
  if (!project?.id) {
    return;
  }

  try {
    await approveSOP(project.id);

    setSnackbar({
      open: true,
      severity: "success",
      message: "SOP approved",
    });

    navigate(approvalReturnPath, {
      replace: true,
    });
  } catch (err) {
    console.error("Failed to approve SOP", err);

    setSnackbar({
      open: true,
      severity: "error",
      message: "Failed to approve SOP",
    });
  }
};

  const openRejectDialog = (projectId) => {
    setRejectSopId(projectId);
    setRejectionReason("");
    setRejectDialogOpen(true);
  };

  const closeRejectDialog = () => {
    setRejectDialogOpen(false);
    setRejectSopId(null);
    setRejectionReason("");
  };

const handleReject = async () => {
  if (!rejectSopId) {
    return;
  }

  const reason = rejectionReason.trim();

  if (!reason) {
    setSnackbar({
      open: true,
      severity: "warning",
      message: "Please provide a reason for rejecting the SOP",
    });

    return;
  }

  try {
    await rejectSOP(rejectSopId, reason);

    setSnackbar({
      open: true,
      severity: "success",
      message: "SOP rejected",
    });

    closeRejectDialog();

    navigate(approvalReturnPath, {
      replace: true,
    });
  } catch (err) {
    console.error("Failed to reject SOP", err);

    setSnackbar({
      open: true,
      severity: "error",
      message: "Failed to reject SOP",
    });
  }
};

  return (
    <Box>
      <Header title="SOP READER" subtitle="Step-by-step guide"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: colors.background.default,
        py: 1.5,
        mb: 2,
        borderBottom: `1px solid ${colors.surface.border}`,
      }}
      />

      {project && (
        <>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              bgcolor: "background.default",
              py: 1,
              mb: 2,
              borderBottom: `1px solid ${colors.surface.border}`,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <Box>
                <Typography variant="h4" sx={{ mb: 0.5 }}>
                  {project.title}
                </Typography>

                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {project.labels?.map((label) => (
                    <StatusChip key={label} label={label} tone="default" />
                  ))}
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <ActionButton
                  tone="teal"
                  variant="outlined"
                  onClick={() =>
                    navigate(approvalReturnPath, {
                      replace: true,
                      state: location.state?.returnState,
                    })
                  }
                >
                  {returnButtonLabel}
                </ActionButton>
                <ActionButton tone="teal" variant="outlined" onClick={handleToggleAll}>
                  {allExpanded ? "Collapse All" : "Expand All"}
                </ActionButton>

                {canEditSop && (
                  <ActionButton
                    variant="contained"
                    tone="teal"
                    onClick={() => navigate(`/sops/edit/${project.id}`)}
                  >
                    Edit SOP
                  </ActionButton>
                )}

                {canApproveSop &&
                  isAssignedApprovalSop &&
                  project.approvalStatus !== "APPROVED" && (
                    <>
                      <Tooltip
                        title={
                          canSubmitApproval
                            ? "Approve SOP"
                            : "You must mark every step as understood before approving"
                        }
                      >
                        <span>
                          <ActionButton
                            tone="success"
                            onClick={handleApproveSop}
                            disabled={!canSubmitApproval}
                          >
                            Approve SOP
                          </ActionButton>
                        </span>
                      </Tooltip>

                      <ActionButton
                        tone="danger"
                        onClick={() => openRejectDialog(project.id)}
                      >
                        Reject SOP
                      </ActionButton>
                    </>
                  )}
              </Box>
            </Box>

            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 4,
                  borderRadius: 999,
                  bgcolor: colors.surface.elevated,
                  "& .MuiLinearProgress-bar": {
                    bgcolor: colors.scalpelTeal[400],
                  },
                }}
              />

              <Typography variant="caption">
                {understoodCount}/{total} steps understood
                {updatedCount > 0 ? ` • ${updatedCount} updated` : ""}
              </Typography>
            </Box>
          </Box>

          {project.description ? (
            <Box sx={{ mb: 3, maxWidth: "900px" }}>
              <Markdown>{project.description}</Markdown>
            </Box>
          ) : null}

          <Box>
            {tasks.map((task, index) => {
              const isExpanded = expandedIds.has(task.id);
              const progress = progressByTaskId[task.id];
              const status = progress?.status || "NEW";
              const chipProps = getProgressChipProps(status);

              return (
                <Accordion
                  key={task.id}
                  expanded={isExpanded}
                  onChange={(_e, next) => {
                    setExpandedIds((prev) => {
                      const ns = new Set(prev);

                      if (next) {
                        ns.add(task.id);
                      } else {
                        ns.delete(task.id);
                      }

                      return ns;
                    });

                    if (next) {
                      handleMarkTaskRead(task.id);
                    }
                  }}
                  disableGutters
                  sx={{
                    mb: 1,
                    bgcolor:
                      status === "UNDERSTOOD"
                        ? colors.surface.raised
                        : colors.surface.card,

                    border: `1px solid ${colors.surface.border}`,
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "none",

                    transition:
                      "all 180ms ease",

                    "&:before": {
                      display: "none",
                    },

                    "&:hover": {
                      bgcolor: colors.surface.raised,
                      borderColor: colors.surface.border,
                      boxShadow: "0 12px 28px rgba(0,0,0,.22)",
                    },

                    "&.Mui-expanded": {
                      margin: "0 0 8px 0",
                      boxShadow: "0 14px 32px rgba(0,0,0,.24)",
                    },
                  }}
                >
                  <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: colors.text.secondary }} />}
                      sx={{
                        minHeight: 56,
                        px: 2,
                        "&.Mui-expanded": {
                          minHeight: 56,
                        },
                        "& .MuiAccordionSummary-content": {
                          my: 1,
                        },
                        "& .MuiAccordionSummary-content.Mui-expanded": {
                          my: 1,
                        },
                      }}
                    >
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ width: "100%", minWidth: 0 }}
                    >
                      <StatusChip label={`Step ${index + 1}`} tone="default" />

                      <Typography
                        sx={{
                          flexGrow: 1,
                          minWidth: 0,
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {task.title}
                      </Typography>

                      <StatusChip label={chipProps.label} tone={chipProps.tone} />

                      {Array.isArray(task.imageUrls) &&
                        task.imageUrls.length > 0 && (
                          <Tooltip
                            title={`View ${task.imageUrls.length} image${
                              task.imageUrls.length > 1 ? "s" : ""
                            }`}
                            arrow
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openImages(task.imageUrls, 0);
                              }}
                            >
                              <Badge
                                badgeContent={task.imageUrls.length}
                                color="secondary"
                                overlap="circular"
                              >
                                <PhotoLibraryIcon fontSize="small" />
                              </Badge>
                            </IconButton>
                          </Tooltip>
                        )}
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails
                      sx={{
                        px: 2,
                        pb: 2,
                        pt: 1,
                        borderTop: `1px solid ${colors.surface.border}`,
                        bgcolor: colors.surface.card,
                      }}
                    >
                    <Box sx={{ mb: 1.5 }}>
                      <Markdown>
                        {task.description || "_No description_"}
                      </Markdown>
                    </Box>

                    <Box
                      display="flex"
                      gap={1}
                      flexWrap="wrap"
                      sx={{ mb: 1 }}
                    >
                      {task.labels?.map((label) => (
                        <StatusChip key={label} label={label} tone="default" />
                      ))}
                    </Box>

                    <Box display="flex" gap={1} justifyContent="flex-end">
                      {status === "UNDERSTOOD" ? (
                        <StatusChip label="✓ Understood" tone="success" />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color={status === "UPDATED" ? "warning" : "secondary"}
                          onClick={() => handleMarkUnderstood(task.id)}
                        >
                          {status === "UPDATED"
                            ? "Re-mark as Understood"
                            : "Mark as Understood"}
                        </Button>
                      )}

                      {canEditSop && (
                        <ActionButton
                          size="small"
                          tone="teal"
                          onClick={() =>
                            navigate(`/tasks/edit/${task.id}`, {
                              state: {
                                redirectPath: `/sops/read/${project.id}`,
                              },
                            })
                          }
                        >
                          Open Task
                        </ActionButton>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </>
      )}

      <TaskImageGalleryModal
        open={imgOpen}
        onClose={() => setImgOpen(false)}
        images={imgList}
        startIndex={imgStartIdx}
        title="Task Images"
      />

      <SOPRejectDialog
        open={rejectDialogOpen}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        onClose={closeRejectDialog}
        onConfirm={handleReject}
      />
    </Box>
  );
}
